<template>
  <div class="profile-wrapper">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10">
          <div class="card profile-card shadow-lg">
            <div class="card-body p-4 p-md-5">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <h2 class="text-uppercase mb-0">
                  <i class="bi bi-person-bounding-box me-2"></i>
                  Perfil do Usuário
                </h2>
                <span class="badge bg-secondary text-uppercase">Editar</span>
              </div>

              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-light" role="status"></div>
                <p class="mt-3">Carregando informações...</p>
              </div>

              <div v-else>
                <form @submit.prevent="submit">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Nome *</label>
                      <input type="text" class="form-control" v-model="form.name" required>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Apelido</label>
                      <input type="text" class="form-control" v-model="form.nickname">
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Email *</label>
                      <input type="email" class="form-control" v-model="form.email" @input="syncLogin" required>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Login *</label>
                      <input type="email" class="form-control" v-model="form.login" readonly required>
                      <small class="text-muted">O login permanece igual ao email.</small>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Senha</label>
                      <input type="password" class="form-control" v-model="form.password" @input="validatePassword"
                        placeholder="Informe para alterar">
                      <div v-if="form.password" class="password-feedback mt-2 small">
                        <div :class="passwordCriteria.length ? 'text-success' : 'text-muted'">
                          <i class="bi" :class="passwordCriteria.length ? 'bi-check-circle-fill' : 'bi-circle'"></i> Mínimo 8 caracteres
                        </div>
                        <div :class="passwordCriteria.uppercase ? 'text-success' : 'text-muted'">
                          <i class="bi" :class="passwordCriteria.uppercase ? 'bi-check-circle-fill' : 'bi-circle'"></i> Uma letra maiúscula
                        </div>
                        <div :class="passwordCriteria.lowercase ? 'text-success' : 'text-muted'">
                          <i class="bi" :class="passwordCriteria.lowercase ? 'bi-check-circle-fill' : 'bi-circle'"></i> Uma letra minúscula
                        </div>
                        <div :class="passwordCriteria.number ? 'text-success' : 'text-muted'">
                          <i class="bi" :class="passwordCriteria.number ? 'bi-check-circle-fill' : 'bi-circle'"></i> Um número
                        </div>
                        <div :class="passwordCriteria.special ? 'text-success' : 'text-muted'">
                          <i class="bi" :class="passwordCriteria.special ? 'bi-check-circle-fill' : 'bi-circle'"></i> Um caractere especial
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Hobbies</label>
                      <input type="text" class="form-control" v-model="form.hobbies">
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Tipo de Conta</label>
                      <input type="text" class="form-control" :value="accountTypeLabel" readonly>
                      <small class="text-muted">Atualiza automaticamente após a confirmação do pagamento.</small>
                    </div>
                    <div class="col-12">
                      <label class="form-label">Descrição</label>
                      <textarea class="form-control" rows="3" v-model="form.description"></textarea>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Foto (máx. 1 MB)</label>
                      <input type="file" class="form-control" accept="image/*" @change="handleFileChange">
                      <small class="text-muted">Enviar nova foto substitui a atual.</small>
                    </div>
                    <div class="col-md-6" v-if="photoPreview">
                      <label class="form-label">Pré-visualização</label>
                      <img :src="photoPreview" alt="Pré-visualização da foto" class="img-fluid rounded shadow-sm"
                        loading="lazy">
                    </div>
                    <div class="col-12">
                      <div class="d-flex justify-content-between align-items-center">
                        <label class="form-label mb-0">Rede de Amigos</label>
                        <small class="text-muted">Controle solicitações, bloqueios e remoções.</small>
                      </div>

                      <div class="row g-3 mt-1">
                        <div class="col-12 col-lg-6">
                          <div class="relationship-card h-100">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                              <h5 class="mb-0">Solicitações recebidas</h5>
                              <span class="badge bg-danger">{{ relationships.incomingRequests.length }}</span>
                            </div>
                            <p v-if="!relationships.incomingRequests.length" class="text-muted small mb-0">Nenhuma solicitação no momento.</p>
                            <div v-else class="d-flex flex-column gap-2">
                              <div v-for="request in relationships.incomingRequests" :key="`incoming-${request.id}`" class="relationship-item">
                                <div>
                                  <strong>{{ request.name }}</strong>
                                  <p class="small mb-1 text-muted">{{ request.email }}</p>
                                  <span v-if="request.nickname" class="badge bg-secondary small-badge">{{ request.nickname }}</span>
                                </div>
                                <div class="d-flex flex-wrap gap-2">
                                  <button type="button" class="btn btn-sm btn-vampire" :disabled="relationshipBusy" @click="handleFriendRequestAction(request.id, 'accept')">
                                    <i class="bi bi-check2"></i> Aceitar
                                  </button>
                                  <button type="button" class="btn btn-sm btn-outline-light" :disabled="relationshipBusy" @click="handleFriendRequestAction(request.id, 'decline')">
                                    <i class="bi bi-x-lg"></i> Recusar
                                  </button>
                                  <button type="button" class="btn btn-sm btn-outline-warning" :disabled="relationshipBusy" @click="blockRequestsFrom(request.requester_id)">
                                    <i class="bi bi-slash-circle"></i> Bloquear pedidos
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="col-12 col-lg-6">
                          <div class="relationship-card h-100">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                              <h5 class="mb-0">Solicitações enviadas</h5>
                              <span class="badge bg-secondary">{{ relationships.outgoingRequests.length }}</span>
                            </div>
                            <p v-if="!relationships.outgoingRequests.length" class="text-muted small mb-0">Você ainda não enviou convites.</p>
                            <div v-else class="d-flex flex-column gap-2">
                              <div v-for="request in relationships.outgoingRequests" :key="`outgoing-${request.id}`" class="relationship-item">
                                <div>
                                  <strong>{{ request.name }}</strong>
                                  <p class="small mb-1 text-muted">{{ request.email }}</p>
                                  <span class="badge bg-dark border">Aguardando resposta</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="col-12 col-xl-6">
                          <div class="relationship-card h-100">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                              <h5 class="mb-0">Meus amigos</h5>
                              <span class="badge bg-success">{{ relationships.friends.length }}</span>
                            </div>
                            <p v-if="!relationships.friends.length" class="text-muted small mb-0">Adicione pessoas pelo email, nome completo ou apelido.</p>
                            <div v-else class="d-flex flex-column gap-2">
                              <div v-for="friend in relationships.friends" :key="`friend-${friend.id}`" class="relationship-item">
                                <div>
                                  <strong>{{ friend.name }}</strong>
                                  <p class="small mb-1 text-muted">{{ friend.email }}</p>
                                  <div class="d-flex gap-2 flex-wrap small">
                                    <span v-if="getBlockState(friend.id).block_chat" class="badge bg-outline-warning">Chat bloqueado</span>
                                    <span v-if="getBlockState(friend.id).block_friend_requests" class="badge bg-outline-warning">Convites bloqueados</span>
                                  </div>
                                </div>
                                <div class="d-flex flex-wrap gap-2">
                                  <button type="button" class="btn btn-sm btn-outline-light" :disabled="relationshipBusy" @click="removeFriendship(friend.id)">
                                    <i class="bi bi-person-dash"></i> Remover
                                  </button>
                                  <button type="button" class="btn btn-sm btn-outline-warning" :disabled="relationshipBusy" @click="toggleChatBlock(friend.id)">
                                    <i class="bi" :class="getBlockState(friend.id).block_chat ? 'bi-unlock' : 'bi-shield-lock'"></i>
                                    {{ getBlockState(friend.id).block_chat ? 'Liberar chat' : 'Bloquear chat' }}
                                  </button>
                                  <button type="button" class="btn btn-sm btn-outline-warning" :disabled="relationshipBusy" @click="toggleRequestBlock(friend.id)">
                                    <i class="bi" :class="getBlockState(friend.id).block_friend_requests ? 'bi-unlock' : 'bi-person-slash'"></i>
                                    {{ getBlockState(friend.id).block_friend_requests ? 'Liberar convites' : 'Bloquear convites' }}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="col-12 col-xl-6">
                          <div class="relationship-card h-100">
                            <div class="d-flex justify-content-between align-items-center mb-2">
                              <h5 class="mb-0">Bloqueios</h5>
                              <span class="badge bg-warning text-dark">{{ relationships.blocks.length }}</span>
                            </div>
                            <p v-if="!relationships.blocks.length" class="text-muted small mb-0">Ninguém bloqueado.</p>
                            <div v-else class="d-flex flex-column gap-2">
                              <div v-for="block in relationships.blocks" :key="`block-${block.blocked_id}`" class="relationship-item">
                                <div>
                                  <strong>{{ block.name }}</strong>
                                  <p class="small mb-1 text-muted">{{ block.email }}</p>
                                  <div class="d-flex gap-2 flex-wrap small">
                                    <span v-if="block.block_chat" class="badge bg-outline-warning">Chat bloqueado</span>
                                    <span v-if="block.block_friend_requests" class="badge bg-outline-warning">Pedidos bloqueados</span>
                                  </div>
                                </div>
                                <button type="button" class="btn btn-sm btn-outline-light" :disabled="relationshipBusy" @click="unblockUser(block.blocked_id)">
                                  <i class="bi bi-unlock"></i> Desbloquear
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div class="col-12">
                          <div class="relationship-card">
                            <h5 class="mb-2">Enviar nova solicitação</h5>
                            <p class="text-muted small">Use email, apelido ou nome completo para localizar a pessoa.</p>
                            <div class="input-group">
                              <input type="text" class="form-control" placeholder="Ex.: alice@email.com ou Príncipe de Paris" v-model="friendIdentifier">
                              <button type="button" class="btn btn-vampire" :disabled="relationshipBusy" @click="submitFriendRequest">
                                <i class="bi bi-send"></i> Enviar pedido
                              </button>
                            </div>
                            <p v-if="relationshipsError" class="text-danger small mt-2 mb-0">{{ relationshipsError }}</p>
                            <p v-if="relationshipsMessage" class="text-success small mt-2 mb-0">{{ relationshipsMessage }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="error" class="alert alert-danger mt-4">{{ error }}</div>
                  <div v-if="success" class="alert alert-success mt-4">{{ success }}</div>

                  <div class="d-flex justify-content-end mt-4">
                    <button type="submit" class="btn btn-vampire" :disabled="saving || !isPasswordValidIfProvided">
                      <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                      <i v-else class="bi bi-save2 me-2"></i>
                      Salvar alterações
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../services/api'
import { validatePassword, isPasswordSecure } from '../utils/validation'
import {
  fetchRelationships,
  sendFriendRequest,
  respondFriendRequest,
  removeFriend,
  updateBlock,
  removeBlock
} from '../services/friends'

export default {
  name: 'UserProfile',
  props: {
    userId: {
      type: [Number, Object],
      default: null
    }
  },
  data() {
    return {
      form: {
        name: '',
        email: '',
        nickname: '',
        login: '',
        password: '',
        description: '',
        hobbies: '',
        photo: null,
        accountType: 'free'
      },
      passwordCriteria: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
      },
      relationships: {
        friends: [],
        incomingRequests: [],
        outgoingRequests: [],
        blocks: []
      },
      relationshipsError: '',
      relationshipsMessage: '',
      relationshipBusy: false,
      friendIdentifier: '',
      loading: true,
      saving: false,
      error: '',
      success: '',
      currentPhotoPath: '',
      photoPreview: ''
    }
  },
  computed: {
    accountTypeLabel() {
      return this.form.accountType === 'premium'
        ? 'Premium — até 5 fichas'
        : 'Gratuita — 1 ficha'
    },
    isPasswordValidIfProvided() {
      if (!this.form.password) return true
      return isPasswordSecure(this.passwordCriteria)
    }
  },
  watch: {
    userId: {
      immediate: true,
      handler() {
        this.fetchData()
      }
    }
  },
  methods: {
    async fetchData() {
      if (!this.userId) {
        this.loading = false
        this.$emit('login-required')
        return
      }

      this.loading = true
      this.error = ''
      try {
        const { data } = await api.get(`/users/${this.userId}`)

        const user = data.user
        this.form.name = user.name || ''
        this.form.email = user.email || ''
        this.form.nickname = user.nickname || ''
        this.form.login = user.login || user.email || ''
        this.form.description = user.description || ''
        this.form.hobbies = user.hobbies || ''
        this.form.accountType = user.account_type || 'free'
        this.form.password = ''
        this.currentPhotoPath = user.photo_path || ''
        this.photoPreview = this.buildPhotoUrl(user.photo_path)
        await this.loadRelationships()
      } catch (error) {
        this.error = error.response?.data?.error || 'Não foi possível carregar os dados do usuário.'
      } finally {
        this.loading = false
      }
    },
    buildPhotoUrl(path) {
      if (!path) {
        return ''
      }
      const baseUrl = (api.defaults.baseURL || '').replace(/\/?api$/, '')
      if (!baseUrl) {
        return path.startsWith('http') ? path : `/${path}`
      }
      return `${baseUrl}/${path}`
    },
    syncLogin() {
      this.form.login = this.form.email
    },
    validatePassword() {
      this.passwordCriteria = validatePassword(this.form.password)
    },
    handleFileChange(event) {
      const [file] = event.target.files
      if (file && file.size > 1024 * 1024) {
        this.error = 'A foto deve ter no máximo 1 MB.'
        event.target.value = ''
        return
      }
      this.form.photo = file || null
      if (file) {
        this.photoPreview = URL.createObjectURL(file)
      } else {
        this.photoPreview = this.buildPhotoUrl(this.currentPhotoPath)
      }
    },
    async loadRelationships() {
      this.relationshipsError = ''
      try {
        const { data } = await fetchRelationships(this.userId)
        this.relationships = {
          friends: data.friends || [],
          incomingRequests: data.incomingRequests || [],
          outgoingRequests: data.outgoingRequests || [],
          blocks: data.blocks || []
        }
      } catch (error) {
        this.relationshipsError = error.response?.data?.error || 'Não foi possível carregar os relacionamentos.'
      }
    },
    getBlockState(userId) {
      const block = this.relationships.blocks.find(entry => entry.blocked_id === userId)
      return {
        block_chat: block?.block_chat || false,
        block_friend_requests: block?.block_friend_requests || false
      }
    },
    async submitFriendRequest() {
      if (!this.friendIdentifier.trim()) {
        this.relationshipsError = 'Informe email, apelido ou nome completo.'
        this.relationshipsMessage = ''
        return
      }

      this.relationshipsError = ''
      this.relationshipsMessage = ''
      this.relationshipBusy = true

      try {
        const { data } = await sendFriendRequest(this.userId, this.friendIdentifier.trim())
        this.relationshipsMessage = data.message || 'Solicitação enviada com sucesso.'
        this.friendIdentifier = ''
        await this.loadRelationships()
      } catch (error) {
        this.relationshipsError = error.response?.data?.error || 'Não foi possível enviar a solicitação.'
      } finally {
        this.relationshipBusy = false
      }
    },
    async handleFriendRequestAction(requestId, action) {
      this.relationshipsError = ''
      this.relationshipsMessage = ''
      this.relationshipBusy = true
      try {
        await respondFriendRequest(this.userId, requestId, action)
        this.relationshipsMessage = action === 'accept'
          ? 'Solicitação aceita e amizade criada.'
          : 'Solicitação recusada.'
        await this.loadRelationships()
      } catch (error) {
        this.relationshipsError = error.response?.data?.error || 'Não foi possível atualizar a solicitação.'
      } finally {
        this.relationshipBusy = false
      }
    },
    async blockRequestsFrom(otherUserId) {
      this.relationshipsError = ''
      this.relationshipsMessage = ''
      this.relationshipBusy = true
      try {
        const { data } = await updateBlock(this.userId, otherUserId, {
          blockChat: this.getBlockState(otherUserId).block_chat,
          blockFriendRequests: true
        })
        if (data.relationships) {
          this.relationships = data.relationships
        } else {
          await this.loadRelationships()
        }
        this.relationshipsMessage = 'Convites deste usuário foram bloqueados.'
      } catch (error) {
        this.relationshipsError = error.response?.data?.error || 'Não foi possível atualizar o bloqueio.'
      } finally {
        this.relationshipBusy = false
      }
    },
    async removeFriendship(friendId) {
      this.relationshipsError = ''
      this.relationshipsMessage = ''
      this.relationshipBusy = true
      try {
        await removeFriend(this.userId, friendId)
        await this.loadRelationships()
        this.relationshipsMessage = 'Amigo removido com sucesso.'
      } catch (error) {
        this.relationshipsError = error.response?.data?.error || 'Não foi possível remover o amigo.'
      } finally {
        this.relationshipBusy = false
      }
    },
    async toggleChatBlock(friendId) {
      const current = this.getBlockState(friendId)
      this.relationshipsError = ''
      this.relationshipsMessage = ''
      this.relationshipBusy = true
      try {
        const { data } = await updateBlock(this.userId, friendId, {
          blockChat: !current.block_chat,
          blockFriendRequests: current.block_friend_requests
        })
        if (data.relationships) {
          this.relationships = data.relationships
        } else {
          await this.loadRelationships()
        }
        this.relationshipsMessage = current.block_chat ? 'Chat liberado para este usuário.' : 'Chat bloqueado para este usuário.'
      } catch (error) {
        this.relationshipsError = error.response?.data?.error || 'Não foi possível atualizar o bloqueio de chat.'
      } finally {
        this.relationshipBusy = false
      }
    },
    async toggleRequestBlock(friendId) {
      const current = this.getBlockState(friendId)
      this.relationshipsError = ''
      this.relationshipsMessage = ''
      this.relationshipBusy = true
      try {
        const { data } = await updateBlock(this.userId, friendId, {
          blockChat: current.block_chat,
          blockFriendRequests: !current.block_friend_requests
        })
        if (data.relationships) {
          this.relationships = data.relationships
        } else {
          await this.loadRelationships()
        }
        this.relationshipsMessage = current.block_friend_requests
          ? 'Convites liberados para este usuário.'
          : 'Convites bloqueados para este usuário.'
      } catch (error) {
        this.relationshipsError = error.response?.data?.error || 'Não foi possível atualizar o bloqueio de convites.'
      } finally {
        this.relationshipBusy = false
      }
    },
    async unblockUser(blockedUserId) {
      this.relationshipsError = ''
      this.relationshipsMessage = ''
      this.relationshipBusy = true
      try {
        const { data } = await removeBlock(this.userId, blockedUserId)
        if (data.relationships) {
          this.relationships = data.relationships
        } else {
          await this.loadRelationships()
        }
        this.relationshipsMessage = 'Usuário desbloqueado.'
      } catch (error) {
        this.relationshipsError = error.response?.data?.error || 'Não foi possível remover o bloqueio.'
      } finally {
        this.relationshipBusy = false
      }
    },
    async submit() {
      if (!this.form.name || !this.form.email || !this.form.login) {
        this.error = 'Nome, email e login são obrigatórios.'
        return
      }

      this.error = ''
      this.success = ''
      this.saving = true

      try {
        const formData = new FormData()
        formData.append('name', this.form.name)
        formData.append('email', this.form.email)
        formData.append('nickname', this.form.nickname)
        formData.append('login', this.form.login)
        formData.append('description', this.form.description)
        formData.append('hobbies', this.form.hobbies)
        if (this.form.password) {
          formData.append('password', this.form.password)
        }
        if (this.form.photo) {
          formData.append('photo', this.form.photo)
        }

        const { data } = await api.put(`/users/${this.userId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })

        this.success = 'Perfil atualizado com sucesso!'
        this.form.password = ''
        this.form.photo = null
        this.currentPhotoPath = data.user.photo_path || ''
        this.photoPreview = this.buildPhotoUrl(data.user.photo_path)
        this.$emit('updated', data.user)
      } catch (error) {
        this.error = error.response?.data?.error || 'Não foi possível atualizar o perfil.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.profile-wrapper {
  padding: 2rem 0 3rem;
}

.profile-card {
  background: rgba(20, 20, 20, 0.95);
  border: 1px solid rgba(218, 165, 32, 0.25);
  color: #f8f9fa;
}

.profile-card .form-control,
.profile-card textarea {
  background-color: rgba(33, 33, 33, 0.85);
  border-color: rgba(139, 0, 0, 0.55);
  color: #f8f9fa;
}

.profile-card .form-control:focus,
.profile-card textarea:focus {
  border-color: var(--vampire-gold);
  box-shadow: 0 0 0 0.2rem rgba(218, 165, 32, 0.25);
}

.profile-card label {
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-size: 0.85rem;
}

.relationship-card {
  background-color: rgba(33, 33, 33, 0.7);
  border: 1px solid rgba(139, 0, 0, 0.4);
  border-radius: 0.75rem;
  padding: 1rem;
  height: 100%;
}

.relationship-item {
  background-color: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(139, 0, 0, 0.35);
  border-radius: 0.5rem;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.small-badge {
  font-size: 0.75rem;
}

.badge.bg-outline-warning {
  background-color: rgba(218, 165, 32, 0.15);
  border: 1px solid rgba(218, 165, 32, 0.5);
  color: #f8f9fa;
}

.btn-vampire {
  background: var(--vampire-red);
  color: #f8f9fa;
  border: none;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.75rem 2.5rem;
}

.btn-vampire:hover:not(:disabled) {
  background: var(--vampire-blood);
}

.btn-vampire:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.password-feedback {
  background: rgba(0, 0, 0, 0.2);
  padding: 0.5rem;
  border-radius: 4px;
}

.password-feedback .text-success {
  color: #2ecc71 !important;
}

.password-feedback .text-muted {
  color: #95a5a6 !important;
}

.img-fluid {
  max-height: 200px;
  object-fit: cover;
}

@media (max-width: 768px) {
  .profile-card {
    padding: 1rem;
  }
}
</style>
